import { useState, useEffect } from "react";
import { toast } from "../Toast";
import { API_URL } from "../config/env";
import { supabase } from "../supabase";
import { styles } from "../styles/appStyles";
import RedeemCodeBlock from "./RedeemCodeBlock";
import { track } from "../lib/analytics";

export default function Paywall({ userId, userEmail, onLogout }) {
  const [loadingPlan, setLoadingPlan] = useState(null);
  useEffect(() => { track("paywall_viewed"); }, []);
  const [autoTriggered, setAutoTriggered] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [refreshMsg, setRefreshMsg] = useState(null);
  const [skipTrial, setSkipTrial] = useState({ lite: false, starter: false, pro: false });

  const truncatedEmail = userEmail && userEmail.length > 25 ? userEmail.slice(0, 25) + "..." : userEmail;

  const handleRefreshSubscription = async () => {
    setRefreshing(true);
    setRefreshMsg(null);
    try {
      const { data: sess } = await supabase.auth.getSession();
      const token = sess?.session?.access_token;
      const r = await fetch(`${API_URL}/sync-subscription`, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...(token ? { Authorization: `Bearer ${token}` } : {}) },
        body: JSON.stringify({ email: userEmail || "", user_id: userId || "" }),
      });
      const d = await r.json();
      if (d.synced) {
        setRefreshMsg("Subscription found! Reloading...");
        setTimeout(() => window.location.reload(), 1000);
      } else {
        setRefreshMsg("No active subscription found for this email. If you just paid, please wait a moment and try again.");
      }
    } catch {
      setRefreshMsg("Could not connect. Please try again or contact support@pitcherly.com");
    }
    setRefreshing(false);
  };

  const handleCheckout = async (plan) => {
    setLoadingPlan(plan);
    const startingTrial = !skipTrial[plan];
    track("upgrade_clicked", { target_plan: plan, skip_trial: !!skipTrial[plan] });
    if (startingTrial) track("trial_started", { target_plan: plan });
    localStorage.removeItem("pitcherly_selected_plan");
    try {
      const res = await fetch(`${API_URL}/create-checkout-session`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          plan,
          user_id: userId || "",
          user_email: userEmail || "",
          skip_trial: !!skipTrial[plan],
        }),
      });
      const data = await res.json();
      if (data.url) {
        track("checkout_started", { target_plan: plan });
        window.location.href = data.url;
      } else {
        toast.error(data.detail || "Failed to create checkout session");
        setLoadingPlan(null);
      }
    } catch {
      toast.error("Could not connect to server");
      setLoadingPlan(null);
    }
  };

  // Auto-trigger checkout if plan was pre-selected during signup
  useEffect(() => {
    const preSelected = localStorage.getItem("pitcherly_selected_plan");
    if (preSelected && !autoTriggered) {
      setAutoTriggered(true);
      handleCheckout(preSelected);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <>
      <style>{styles}</style>
      <div className="paywall" style={{position:"relative"}}>
        <div style={{position:"absolute",top:"max(20px, env(safe-area-inset-top))",right:"max(16px, env(safe-area-inset-right))",fontSize:13,color:"#888",zIndex:10}}>
          {truncatedEmail && <span>logged in as {truncatedEmail} &middot; </span>}
          <button
            onClick={onLogout}
            style={{background:"none",border:"none",color:"#888",fontSize:13,cursor:"pointer",padding:"8px 10px",minHeight:44,display:"inline-flex",alignItems:"center",fontFamily:"inherit"}}
            onMouseEnter={e => e.target.style.color = "#e8e4dd"}
            onMouseLeave={e => e.target.style.color = "#888"}
          >Sign out</button>
        </div>
        <div className="paywall-logo">Pitcher<em>ly</em></div>
        <div className="paywall-headline">The AI writing toolkit built for real estate agents</div>
        <div className="paywall-sub">Save 5-10 hours a week. Pays for itself with one extra deal.</div>
        <div className="plan-cards">
          <div className="plan-card" style={{width:280,padding:24,opacity:0.92,borderColor:"rgba(255,255,255,0.06)"}}>
            <div className="plan-name" style={{fontSize:20}}>Pitcherly Lite</div>
            <div className="plan-price">$19<span>/month</span></div>
            <ul className="plan-features">
              <li>50 generations / month</li>
              <li>All 10 writing tools</li>
              <li>No property lookups</li>
            </ul>
            <button className="plan-cta secondary" onClick={() => handleCheckout("lite")} disabled={!!loadingPlan}>
              {loadingPlan === "lite" ? "Redirecting..." : skipTrial.lite ? "Subscribe Now →" : "Start free trial"}
            </button>
            <div style={{display:"flex",alignItems:"center",gap:8,marginTop:10,justifyContent:"center"}}>
              <input
                type="checkbox"
                id="skip-trial-lite"
                checked={skipTrial.lite}
                onChange={e => setSkipTrial(prev => ({ ...prev, lite: e.target.checked }))}
                style={{width:14,height:14,minWidth:14,minHeight:14,accentColor:"#d4915c",flexShrink:0,cursor:"pointer"}}
              />
              <label htmlFor="skip-trial-lite" style={{fontSize:12,color:"#888",cursor:"pointer"}}>
                Skip free trial — start paying today
              </label>
            </div>
          </div>
          <div className="plan-card">
            <div className="plan-name">Pitcherly Starter</div>
            <div className="plan-price">$49<span>/month</span></div>
            <ul className="plan-features">
              <li>50 generations during 7-day trial</li>
              <li>200 generations / month after trial</li>
              <li>All 10 writing tools</li>
              <li>10 verified property lookups / month</li>
              <li>Market-data CMAs (live estimate + comps)</li>
              <li>Email support</li>
            </ul>
            <button className="plan-cta secondary" onClick={() => handleCheckout("starter")} disabled={!!loadingPlan}>
              {loadingPlan === "starter" ? "Redirecting..." : skipTrial.starter ? "Subscribe Now →" : "Start free trial"}
            </button>
            <div style={{display:"flex",alignItems:"center",gap:8,marginTop:10,justifyContent:"center"}}>
              <input
                type="checkbox"
                id="skip-trial-starter"
                checked={skipTrial.starter}
                onChange={e => setSkipTrial(prev => ({ ...prev, starter: e.target.checked }))}
                style={{width:14,height:14,minWidth:14,minHeight:14,accentColor:"#d4915c",flexShrink:0,cursor:"pointer"}}
              />
              <label htmlFor="skip-trial-starter" style={{fontSize:12,color:"#888",cursor:"pointer"}}>
                Skip free trial — start paying today
              </label>
            </div>
          </div>
          <div className="plan-card featured">
            <div className="plan-badge">Most Popular</div>
            <div className="plan-name">Pitcherly Pro</div>
            <div className="plan-price">$99<span>/month</span></div>
            <ul className="plan-features">
              <li>Unlimited generations</li>
              <li>All 10 writing tools</li>
              <li>50 verified property lookups / month</li>
              <li>Market-data CMAs (live estimate + comps)</li>
              <li>Priority support</li>
              <li>Early access to new features</li>
            </ul>
            <button className="plan-cta primary" onClick={() => handleCheckout("pro")} disabled={!!loadingPlan}>
              {loadingPlan === "pro" ? "Redirecting..." : skipTrial.pro ? "Subscribe Now →" : "Start free trial"}
            </button>
            <div style={{display:"flex",alignItems:"center",gap:8,marginTop:10,justifyContent:"center"}}>
              <input
                type="checkbox"
                id="skip-trial-pro"
                checked={skipTrial.pro}
                onChange={e => setSkipTrial(prev => ({ ...prev, pro: e.target.checked }))}
                style={{width:14,height:14,minWidth:14,minHeight:14,accentColor:"#d4915c",flexShrink:0,cursor:"pointer"}}
              />
              <label htmlFor="skip-trial-pro" style={{fontSize:12,color:"#888",cursor:"pointer"}}>
                Skip free trial — start paying today
              </label>
            </div>
          </div>
        </div>
        <div className="paywall-fine">7-day free trial. Cancel anytime. No commitment.</div>
        <div style={{marginTop:20,textAlign:"center"}}>
          <button
            onClick={handleRefreshSubscription}
            disabled={refreshing}
            style={{background:"none",border:"none",color:"#888",fontSize:13,cursor:"pointer",padding:0,fontFamily:"inherit",textDecoration:"underline"}}
          >
            {refreshing ? "Checking..." : "Already subscribed? Click here to refresh"}
          </button>
          {refreshMsg && <div style={{marginTop:10,fontSize:12,color:"#888",maxWidth:400,marginLeft:"auto",marginRight:"auto"}}>{refreshMsg}</div>}
        </div>
        <div style={{maxWidth:420,margin:"32px auto 0"}}>
          <RedeemCodeBlock userId={userId} userEmail={userEmail} />
        </div>
      </div>
    </>
  );
}
